import { useMemo } from 'react';
import { Smartphone, Printer, Monitor, Tablet, Tv, Phone, Video, Clock, Boxes, MapPin } from 'lucide-react';
import { KpiCard } from '../KpiCard';
import { DonutChart } from '../DonutChart';
import { CategoryRanking } from '../CategoryRanking';
import { SiteBars } from '../SiteBars';
import { WidgetCard, SectionTitle, Legend, EmptyMsg, useBarsIn, DashSkeleton } from '../primitives';

const CATS = [
  { key: 'cellphones', name: 'Celulares', Icon: Smartphone, color: '#00b8d4', link: '/micro/mobile-devices' },
  { key: 'printers', name: 'Impresoras', Icon: Printer, color: '#f59e0b', link: '/micro/printers' },
  { key: 'workstations', name: 'Computadoras', Icon: Monitor, color: '#3b82f6', link: '/micro/workstations' },
  { key: 'monitors', name: 'Monitores', Icon: Monitor, color: '#a855f7', link: '/micro/monitores' },
  { key: 'tablets', name: 'Tablets', Icon: Tablet, color: '#22c55e', link: '/micro/tablets' },
  { key: 'tvs', name: 'Televisores', Icon: Tv, color: '#8b5cf6', link: '/micro/tvs' },
  { key: 'ip_phones', name: 'Teléfonos IP', Icon: Phone, color: '#ec4899', link: '/micro/ip-phones' },
  { key: 'projectors', name: 'Proyectores', Icon: Video, color: '#14b8a6', link: '/micro/proyectores' },
  { key: 'clocks', name: 'Relojes', Icon: Clock, color: '#64748b', link: '/micro/clocks' },
];
const has = (stats, key, allowed) => !!stats && allowed[key] && Object.prototype.hasOwnProperty.call(stats, key);

export const MicroDashboard = ({ loading, stats, bySite, allowed }) => {
  const barsIn = useBarsIn(loading);
  const ranking = useMemo(() => CATS.filter(item => has(stats, item.key, allowed))
    .map(item => ({ ...item, value: stats[item.key] })).sort((a, b) => b.value - a.value), [stats, allowed]);
  const total = ranking.reduce((sum, item) => sum + item.value, 0);
  const mobile = ranking.filter(item => ['cellphones', 'tablets'].includes(item.key)).reduce((sum, item) => sum + item.value, 0);
  const other = total - mobile;
  const composition = ranking.filter(item => item.value > 0);
  const mobility = [
    { name: 'Celulares y tablets', value: mobile, color: '#00b8d4' },
    { name: 'Otros dispositivos', value: other, color: '#8b5cf6' },
  ].filter(item => item.value > 0);
  const hasMobile = has(stats, 'cellphones', allowed) || has(stats, 'tablets', allowed);

  if (loading) return <DashSkeleton />;

  return (
    <div className="space-y-6">
      {stats && ranking.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <KpiCard testid="kpi-card-micro-total" title="Total Dispositivos" value={total} subtitle="Categorías disponibles" Icon={Boxes} to={allowed.inventory ? '/reports/inventory' : undefined} />
        {has(stats, 'cellphones', allowed) && <KpiCard testid="kpi-card-micro-celulares" title="Celulares" value={stats.cellphones} subtitle={`${total ? ((stats.cellphones / total) * 100).toFixed(1) : 0}% del total mostrado`} Icon={Smartphone} iconClass="bg-blue-500/10 text-blue-500" to="/micro/mobile-devices" delay={80} />}
        {has(stats, 'printers', allowed) && <KpiCard testid="kpi-card-micro-impresoras" title="Impresoras" value={stats.printers} subtitle="Parque de impresión" Icon={Printer} iconClass="bg-amber-500/10 text-amber-500" to="/micro/printers" delay={160} />}
        {has(stats, 'workstations', allowed) && <KpiCard testid="kpi-card-micro-computadoras" title="Computadoras" value={stats.workstations} subtitle="Puestos de trabajo" Icon={Monitor} iconClass="bg-purple-500/10 text-purple-500" to="/micro/workstations" delay={240} />}
      </div>}

      {ranking.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <WidgetCard testid="widget-micro-composition" className="lg:col-span-5" delay={120}>
          <SectionTitle title="Distribución de Dispositivos" subtitle="Proporción por categoría disponible" />
          {composition.length ? <><DonutChart data={composition} centerValue={total} centerLabel="Equipos" /><Legend items={composition} /></> : <EmptyMsg />}
        </WidgetCard>
        <WidgetCard testid="widget-micro-ranking" className="lg:col-span-7" delay={180}>
          <SectionTitle title="Desglose por Categoría" subtitle="Ranking de dispositivos por volumen" />
          <CategoryRanking items={ranking} total={total} barsIn={barsIn} />
        </WidgetCard>
      </div>}

      {(hasMobile || bySite !== null) && <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {hasMobile && <WidgetCard testid="widget-micro-mobility" className="lg:col-span-5" delay={220}>
          <SectionTitle title="Celulares y tablets" subtitle="Comparación con otros dispositivos disponibles" />
          {mobility.length ? <div className="flex items-center gap-4">
            <div className="w-1/2"><DonutChart data={mobility} centerValue={total} centerLabel="Total" innerRadius={46} outerRadius={70} height={180} /></div>
            <div className="w-1/2 space-y-2">{mobility.map(item => <div key={item.name} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-foreground flex-1">{item.name}</span>
              <span className="text-xs font-semibold font-mono">{item.value}</span>
              <span className="text-[11px] text-muted-foreground font-mono w-11 text-right">{total ? ((item.value / total) * 100).toFixed(1) : 0}%</span>
            </div>)}</div>
          </div> : <EmptyMsg />}
        </WidgetCard>}
        {bySite !== null && <WidgetCard testid="widget-micro-site-distribution" className={hasMobile ? 'lg:col-span-7' : 'lg:col-span-12'} delay={280}>
          <SectionTitle title="Distribución por Site" subtitle={`Categorías disponibles · Top ${Math.min(bySite.length, 8)}`} Icon={MapPin} />
          {bySite.length ? <SiteBars data={bySite} barsIn={barsIn} testidPrefix="micro-site" /> : <EmptyMsg />}
        </WidgetCard>}
      </div>}
    </div>
  );
};
